import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../../api/axios";
import toast from "react-hot-toast";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ProjectSelector } from "../../../components/utils/moduleSelectors/projectSelector";
import { DocumentUploadSectionV5 } from "../../../components/dashboard/Media/DocumentUploadSectionV5";
import { PMField } from "../../../components/utils/PMField";

export const DrawingEditor = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [uploadedDocument, setUploadedDocument] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    project_id: null,
    floor: "",
    house: "",
    document_id: null,
  });

  useEffect(() => {
    if (isEdit) {
      loadDrawing();
    }
  }, [id]);

  const loadDrawing = async () => {
    try {
      setLoading(true);
      const response = await axios({
        method: "get",
        url: `/drawings/${id}`,
      });
      const drawing = response.data;
      setFormData({
        title: drawing.title || "",
        description: drawing.description || "",
        project_id: drawing.project?.id || null,
        floor: drawing.floor || "",
        house: drawing.house || "",
        document_id: drawing.document?.id || null,
      });
      if (drawing.project) {
        setSelectedProject({
          id: drawing.project.id,
          displayTitle: drawing.project.displayTitle,
        });
      }
      if (drawing.document) {
        setUploadedDocument(drawing.document);
      }
    } catch (err) {
      console.error("Error loading drawing:", err);
      toast.error(t("drawings.load_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      toast.error(t("drawings.title.required"));
      return;
    }

    if (!formData.document_id) {
      toast.error(t("drawings.document.required"));
      return;
    }

    try {
      setSaving(true);
      const payload = {
        drawing: {
          title: formData.title,
          description: formData.description,
          project_id: formData.project_id,
          floor: formData.floor,
          house: formData.house,
          document_id: formData.document_id,
        },
      };

      let response;
      if (isEdit) {
        response = await axios({
          method: "put",
          url: `/drawings/${id}`,
          data: payload,
        });
        toast.success(t("drawings.updated"));
      } else {
        response = await axios({
          method: "post",
          url: "/drawings/",
          data: payload,
        });
        toast.success(t("drawings.created"));
      }

      navigate(`/dashboard/drawings/${response.data.id}`);
    } catch (err) {
      console.error("Error saving drawing:", err);
      toast.error(isEdit ? t("drawings.update_error") : t("drawings.create_error"));
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpload = (documents) => {
    if (documents && documents.length > 0) {
      const doc = documents[0];
      setUploadedDocument(doc);
      setFormData((prev) => ({
        ...prev,
        document_id: doc.id,
      }));
    }
  };

  if (loading) {
    return <ResourceLoading />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader
        title={isEdit ? t("drawings.edit") : t("drawings.new")}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
          <div className="space-y-6">
            <PMField
              name="title"
              type="text"
              label={t("drawings.title")}
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              required
            />

            <PMField
              name="description"
              type="textarea"
              label={t("drawings.description")}
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, description: e.target.value }))
              }
            />

            <div>
              <ProjectSelector
                name="project_id"
                selected={selectedProject}
                onChange={(e) => {
                  const projectId = e.target.value;
                  const project = e.target.data;
                  setSelectedProject(project ? { id: project.id, displayTitle: project.displayTitle } : null);
                  setFormData((prev) => ({ ...prev, project_id: projectId || null }));
                }}
                label={t("drawings.project")}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <PMField
                name="floor"
                type="text"
                label={t("drawings.floor")}
                value={formData.floor}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, floor: e.target.value }))
                }
                placeholder={t("drawings.floor.placeholder")}
              />

              <PMField
                name="house"
                type="text"
                label={t("drawings.house")}
                value={formData.house}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, house: e.target.value }))
                }
                placeholder={t("drawings.house.placeholder")}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("drawings.document")}
                {!uploadedDocument && <span className="text-red-500 ml-1">*</span>}
              </label>
              {uploadedDocument ? (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">{uploadedDocument.title || "Document"}</p>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadedDocument(null);
                      setFormData((prev) => ({ ...prev, document_id: null }));
                    }}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <DocumentUploadSectionV5
                  multiple={false}
                  module="drawing"
                  parentCallback={handleDocumentUpload}
                  required
                />
              )}
            </div>

            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                {t("Cancel")}
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {saving ? t("Saving...") : (isEdit ? t("Update") : t("Create"))}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

